/**
 * @ngdoc Directive
 * @name vrmGraphWidget
 * @example
 *
 * <vrmGraphWidget type="BatteryGraph" />
 *
 */
import angular from 'angular';
import { mixinZoomState } from '@/angular/modules/vrm-components/graph-widget/zoomstate';
import { cloneDeep, sortBy } from 'lodash';
import { handleLocalStorageBool, handleInitialLocalStorageBool } from '@/angular/utils/handleLocalStorage'

angular.module('vrm.components')
    .directive('vrmGraphWidget', vrmGraphWidget);

// Filter out null values for specific cases
const FILTER_NULL_POINTS_ATTRIBUTE_IDS = [
    1760,                                   // EV target SOC
];
// Optionally, also add here to have only horizontal lines and make any changes appear sudden
const FILTER_NULL_POINTS_ATTRIBUTE_IDS_HORIZONAL_LINE = [
    1760,                                   // EV target SOC
];


/* @ngInject */
function vrmGraphWidget() {
    return {
        restrict: 'E',
        template: require('@/angular/modules/vrm-components/graph-widget/graph-widget.template.html'),
        controller,
        scope: {
            iconClass: '=',
            customName: '=',
            type: '=',
            loading: '=',
            instance: '=',
            title: '=',
            subTitle: '=',
            tooltip: '=',
            data: '=',
            zoomstate: '=',
            range: '=',
            widgetId: '=',
            isMobile: '=',
            hasTouchEnabled: '=',
            size: '=',
            itemState: '=?',
            persistentMode: '=',
            defaultShowRangeValues: '=?',
        }
    };

    /* @ngInject */
    function controller($scope, $q, GraphService, locale, WhitelabelService, localStorageService, AdvancedGraphWidgetConfigs, $stateParams) {
        let loaded = false;
        $scope.inview = false;
        $scope.setWatcher = true;

        function updateTabsList() {
            const tabs = $scope.tabsMeta;
            const attributeCodeToId = code => Object.keys($scope.data.meta).find(key => $scope.data.meta[key].code === code);
            const attributeHasData = idAttr => (idAttr in $scope.data.data) && $scope.data.data[idAttr].length > 0;
            const tabHasData = tab => Object.keys(tab.fields).map(attributeCodeToId).some(attributeHasData);
            const getTabList = () => {
                switch ($scope.type) {
                    default: {
                        return tabs;
                    }
                    case 'AcInputVoltageAndCurrent':
                    case 'AcOutputVoltageAndCurrent':
                    case 'InverterChargerACInputVoltageAndCurrent':
                    case 'InverterChargerACOutputVoltageAndCurrent':
                    case 'ChargerOutputVoltageAndCurrent': {
                        const validPhaseTabs = tabs.filter(tabHasData);
                        return validPhaseTabs.length ? validPhaseTabs : [tabs[0]];
                    }
                }
            };

            $scope.meta = $scope.data ? getTabList() : tabs;
        }

        $scope.$watch('meta', function () {
            if ($scope.meta && $scope.meta.length) {
                $scope.current = $scope.meta[0];
            }
        });

        $scope.$watch('type', function () {
            $scope.tabsMeta = AdvancedGraphWidgetConfigs[$scope.type];
            updateTabsList();
        });

        $scope.$watch('size', () => {
            if (!$scope.chart || !$scope.chart.getHighcharts) return;
            $scope.chart.getHighcharts().reflow();
        });

        function getCurrent() {
            return $scope.current
        };

        $scope.setCurrent = function (meta) {
            if (!(meta === getCurrent())) {
                Object.keys(meta.extraOptions.yAxis).forEach(key => {
                    delete meta.extraOptions.yAxis[key].min;
                    delete meta.extraOptions.yAxis[key].max;
                    delete meta.extraOptions.yAxis[key].tickAmount;
                })
                $scope.current = meta;
                $scope.chart = GraphService.getAdvancedConfig($scope.current, getGraphColors());
            }
            //Make the same steps as when loading using load() so it has a consistent behaviour

            // Reset the axis when the chart is loaded
            for (let axis of $scope.chart.yAxis) {
                axis.max = null;
            }
        };

        $scope.$watch('loading', function (loading) {
            if ($scope.chart) {
                $scope.chart.loading = loading;
            }
        });

        $scope.formatSeriesTitle = function (meta) {
            let result = '';
            if (meta.description) {
                result += ` ${meta.description}`;
            }
            if (meta.formatWithUnit) {
                const unit = meta.formatWithUnit
                    .replace(meta.formatValueOnly, '')
                    .replace('%%', '%')
                    .replace(/%\.\dF/, '')
                    .trim();
                if (unit) {
                    result += ` (${unit})`;
                }
            }
            return result;
        };

        $scope.toggle = function ($index) {
            const series = $scope.chart.series[$index];
            series.visible = !series.visible;
            updateStorageLabels()
        };

        $scope.seriesLabelStyle = function ($index) {
            const colors = WhitelabelService.getGraphColors();
            const colorByIndex = [
                colors["blue"],
                colors["orange"],
                colors["black"],
                colors["green"],
            ];
            let opacity = 1;
            if (
                $scope.chart &&
                $scope.chart.series[$index] &&
                !$scope.chart.series[$index].visible
            ) {
                opacity = 0.5;
            }
            return { color: colorByIndex[$index], opacity: opacity };
        };

        $scope.widgetTitle = function () {
            let title = $scope.title;
            if ($scope.customName) {
                title = `${$scope.customName} - ` + title;
            }
            if (parseInt($scope.instance)) {
                title += ` [${$scope.instance}]`;
            }
            return title;
        };

        $scope.popup = {
            elem: null,
            active: false,
            widgetVisible: true,
            useMinMax: null,
            clicked: false,
        };

        $scope.onOutsidePopupClick = () => {
            if ($scope.popup.active) {
                $scope.popup.active = false;
                $scope.popup.clicked = true;
                $scope.$applyAsync();
                //this allows it to opened after closing it by clicking outside
                setTimeout(() => { $scope.popup.clicked = false; $scope.$applyAsync(); }, 1);
                return true;
            } else {
                return false;
            }
        };

        $scope.clickSettings = function () {
            if (!$scope.popup.clicked && !$scope.popup.active) {
                $scope.popup.active = true;
                $scope.popup.clicked = true;
                $scope.$applyAsync();
            }
        };

        $scope.$watch('popup.widgetVisible', (isVisible) => {
            if ($scope.itemState) $scope.itemState.active = isVisible;
        });

        $scope.clickExpand = () => {
            if ($scope.itemState) $scope.itemState.expanded = !$scope.itemState.expanded;
        }

        /**
         * Remove the null points from the data points of graphs:
         * This is done, because null points break the line of a graph
         */
        function filterNullPoints(data) {
            const res = { ...data }
            Object.keys(data).forEach(function (key) {
                const dataAttributeId = parseInt(key.split("_")[0]);
                if (FILTER_NULL_POINTS_ATTRIBUTE_IDS.includes(dataAttributeId)) {
                    const filteredDataPoints = data[key].filter((v) => v[1] !== null);

                    // For when we not only want to filter nulls out, but also want the resulting line to always be horizontal.
                    // We insert a datapoint right before a change, containing the previous value, so that any change appears to be sudden.
                    if (FILTER_NULL_POINTS_ATTRIBUTE_IDS_HORIZONAL_LINE.includes(dataAttributeId)) {
                        const newPoints = [];
                        for (let i = 0; i < filteredDataPoints.length; i++) {
                            const [timestamp, value] = filteredDataPoints[i];

                            // Check for change from previous point
                            if (i > 0 && filteredDataPoints[i - 1][1] !== value) {
                                const prevTimestamp = filteredDataPoints[i - 1][0];
                                const prevValue = filteredDataPoints[i - 1][1];

                                // Insert new point 1 second before the current one
                                newPoints.push([timestamp - 1, prevValue]);
                            }
                            // Add the current point
                            newPoints.push([timestamp, value]);
                        }
                        res[key] = newPoints;
                    } else {
                        res[key] = filteredDataPoints;
                    }
                }
            });
            return res;
        }

        $q.all([GraphService.ready(), locale.ready('advanced')]).then(function (_, i) {
            function load() {
                if ($scope.inview && !loaded && $scope.data) {

                    const currentCopy = cloneDeep($scope.current)


                    // inject virtual fields into data and their computed values
                    const { virtualFields } = $scope.current;
                    if (virtualFields) {
                        for (let virtualField in virtualFields) {
                            $scope.data.meta[`virtual_field_${virtualField}`] = virtualFields[virtualField].meta;
                            $scope.data.data[`virtual_field_${virtualField}`] = virtualFields[virtualField].getData($scope.data.data, $scope.data.meta);
                        }
                        currentCopy.fields = GraphService.addVirtualFields(currentCopy);
                    }
                    loaded = true;

                    const initialFields = { ...currentCopy.fields };
                    Object.keys($scope.data.data).forEach(key => {
                        if (!$scope.data.data[key].length) {
                            const seriesCode = $scope.data.meta[key].code
                            delete currentCopy.fields[seriesCode]
                        }
                    })

                    const isSoC = Object.keys(currentCopy).includes('fields') &&
                        (Object.keys(currentCopy.fields).includes('bs') || Object.keys(currentCopy.fields).includes('SOC'))
                    if (Object.keys(currentCopy).includes('extraOptions')
                        && Object.keys(currentCopy.extraOptions).includes('yAxis')
                        && !(isSoC) // so it includes the min 0 max 100 in the graph
                    ) {
                        // Unset y-axis min-max values because it can be a deepclone from a custom widget
                        Object.keys(currentCopy.extraOptions.yAxis).forEach(key => {
                            delete currentCopy.extraOptions.yAxis[key].min;
                            delete currentCopy.extraOptions.yAxis[key].max;
                            delete currentCopy.extraOptions.yAxis[key].tickAmount;
                        })
                    }

                    if (Object.keys(currentCopy.fields).length < 3 && Object.keys(currentCopy.fields).includes('TOTAL') && !currentCopy.totalOnly) {
                        delete currentCopy.fields['TOTAL']
                    }

                    // Never change initial loaded chart
                    if (!$scope.chart) {
                        setChartInitial(currentCopy, initialFields);
                    }


                    // Prevent mutation of injected $scope.data.meta
                    // The metas may get modified by $scope.current.preprocessMeta
                    // This system expects the $scope.metas to contain an array of objects which are the same (references) as stored in $scope.data.meta
                    // The reference requirements comes from the tooltips which use $scope.data.meta as datasource but need the instances to be parsed
                    // However, the [data] scope item is shared between the widgets so we can't modify the original as that would pollute other widgets.
                    // </rant>
                    const parsedMetas = cloneDeep($scope.data.meta);
                    $scope.metas = [];
                    if (!currentCopy.totalOnly) {
                        $scope.metas = Object.values(parsedMetas).filter(function (field) {
                            return currentCopy.fields.hasOwnProperty(field.code);
                        });
                        $scope.metas = sortBy($scope.metas, function (meta) {
                            if (currentCopy.fields[meta.code] && currentCopy.fields[meta.code].yAxis)
                                return currentCopy.fields[meta.code].yAxis;
                            return 0;
                        });
                    }

                    if (currentCopy.allowSum && !currentCopy.totalOnly && Object.keys(currentCopy.fields).includes('TOTAL')) {
                        $scope.metas.push({
                            'description': locale.getString('advanced.total'),
                            'formatWithUnit': '',
                            'formatValueOnly': ''
                        })
                    }

                    if (currentCopy.preprocessMeta) {
                        $scope.metas = currentCopy.preprocessMeta($scope.metas);
                    }

                    if ($scope.type == "SolarIrradiance") {
                        $scope.chart.options.plotOptions.series.connectNulls = true;
                    }
                    const res = GraphService.updateConfig(
                        $scope.chart,
                        filterNullPoints($scope.data.data),
                        parsedMetas,
                        initialFields,
                        !!currentCopy.allowSum,
                        !!currentCopy.totalOnly,
                        $scope.range,
                        currentCopy.preprocessValue,
                        $scope.popup.useMinMax,
                    );
                    if (res === -1) {
                        // some race condition caused the meta (graph legends) and series (graph lines) to mismatch
                        // often caused by quickly turning different widgets on/off
                        // set the widget back to the initial configuration
                        setChartInitial(currentCopy, initialFields);
                    }
                    mixinZoomState($scope, $scope.setWatcher);
                    $scope.setWatcher = false;
                }
            }

            $scope.$watchGroup(['current', 'popup.useMinMax'], function () {
                loaded = false;
                updateStorageRanges();
                load();
            });

            $scope.$watch('data', () => {
                updateTabsList();
                loaded = false;
                updateStorageRanges();
                load();
            });

            $scope.$watchGroup(['inview'], load);
        });

        function setChartInitial(currentCopy, initialFields) {
            // fields can be deleted if there is no data
            // but we want initial config to be based on all possible fields
            $scope.chart = GraphService.getAdvancedConfig(
                {
                    ...currentCopy,
                    fields: initialFields,
                },
                getGraphColors()
            );
            initial_visibility()
            // Use prop to determine default value for "show range values"
            const storedValue = localStorageService.get(storageKey());
            if (storedValue && storedValue.hasOwnProperty("use_range")) {
                $scope.popup.useMinMax = storedValue["use_range"];
            } else {
                // Default based on the defaultShowRangeValues prop (defaults to false if not set)
                $scope.popup.useMinMax = $scope.defaultShowRangeValues === true;
            }
        }

        function updateStorageLabels() {
            const visibilities = $scope.chart.series.map(x => x.visible);
            if ($scope.current.allowSum && !$scope.current.totalOnly) {
                visibilities[visibilities.length - 1] = !visibilities[visibilities.length - 1]
            }
            const all_visibile = visibilities.every(x => x);
            if (all_visibile) {
                const old_value = localStorageService.get(storageKey());
                if (old_value && old_value.labels) {
                    delete old_value.labels;
                    if (Object.keys(old_value).length) {
                        localStorageService.set(storageKey(), old_value);
                    } else {
                        localStorageService.remove(storageKey());
                    }
                }
            } else {
                const old_value = localStorageService.get(storageKey()) || {}
                if ($scope.current.allowSum && !$scope.current.totalOnly) {
                    visibilities[visibilities.length - 1] = !visibilities[visibilities.length - 1]
                }
                old_value.labels = visibilities;
                localStorageService.set(storageKey(), old_value)
            }
        }

        function updateStorageRanges() {
            let old_value = localStorageService.get(storageKey())

            if ($scope.popup.useMinMax === null) { // value === null
                // no value in parameter is updated with the local storage value
                if (old_value && old_value.hasOwnProperty("use_range")) {
                    $scope.popup.useMinMax = old_value["use_range"] // initial value
                } else {
                    // Default based on the defaultShowRangeValues prop (defaults to false if not set)
                    $scope.popup.useMinMax = $scope.defaultShowRangeValues === true;
                }
            }
            else {
                const result = handleLocalStorageBool( old_value, $scope.popup.useMinMax, "use_range")
                if(result){
                    localStorageService[result.action](storageKey(), result.value);
                }
            }
        }

        function storageKey() {
            return ['widget_settings', $stateParams.id, $scope.widgetId, $scope.instance].join('_');
        }

        function getGraphColors() {
            if (!$scope.current.getColors) {
                return null;
            }
            return $scope.current.getColors(WhitelabelService.getGraphColors());
        }

        function initial_visibility() {
            const local_settings = localStorageService.get(storageKey());
            if (local_settings && local_settings.labels) {
                const active_subtitles = local_settings.labels;
                if (active_subtitles && active_subtitles.length === $scope.chart.series.length) {
                    for (let i = 0; i < active_subtitles.length; i++) {
                        $scope.chart.series[i].visible = active_subtitles[i];
                    }
                }
            }
        }
    }
}
